/**
 * @file GenerateReportPage.js
 * @description Common Generate Report page with shared fields and report-specific inputs.
 */

import { useEffect, useMemo, useState } from 'react';
import { format, subDays } from 'date-fns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import '../page-list/PageList.css';
import './EventLogReportParameters.scss';

import CustomDatePicker from '../helpers/CustomDatePicker_V2';
import { formatDateToMMDDYYYY } from '../helpers/DateUtils';
import { CharacterLimitIndicator } from '../helpers/CharacterLimitIndicator';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import EventLogReportParameters from './EventLogReportParameters';
import IndividualsReportParameters from './IndividualsReportParameters';
import UsersReportParameters from './UsersReportParameters';
import MostVisitedPagesReportParameters from './MostVisitedPagesReportParameters';
import MostClickedContentReportParameters from './MostClickedContentReportParameters';
import AudienceBadge from '../shared/AudienceBadge';
import Toast from '../ui/toasts/Toast';
import { ROUTES } from '../../constants/routes';
import { useAuthContext } from '../../store/AuthContext';
import { useAdminAudienceEnabled } from '../../hooks/useAdminAudienceEnabled';
import { fetchPopulationGroups } from '../../services/populationGroupService';
import {
    fetchEmploymentCategories,
    fetchEmploymentStatuses,
    fetchTenantUserStatuses,
} from '../../services/systemService';
import { fetchClientAdminUsers } from '../../services/clientService';
import {
    createGeneratedReport,
    fetchReportDefinitions,
    fetchGeneratedReportById,
} from '../../services/reportingService';
import { ReportDefinitionCodes, UserType } from '../../utils/data/enums';
import { logEvent } from '../../services/loggingService';
import { LogEventType } from '../../utils/data/enums';
import { getText } from '../../i18n';

function normalizeLegacyReportId(reportId) {
    return String(reportId || '')
        .replace(/^rpt-0*/i, '')
        .trim();
}

function mapAdminNamesToOptions(adminNames, adminUsers) {
    return (adminNames || []).map((adminName, index) => {
        const knownAdmin = (adminUsers || []).find(
            admin => admin.name === adminName,
        );

        if (knownAdmin) {
            return knownAdmin;
        }

        return {
            id: `readonly-admin-${index}`,
            name: adminName,
        };
    });
}

function getArraySelection(rawValue) {
    if (!rawValue) {
        return [];
    }

    if (Array.isArray(rawValue)) {
        return rawValue
            .map(item => {
                if (item && typeof item === 'object') {
                    return item.id || item.name || '';
                }

                return item;
            })
            .filter(Boolean)
            .map(String);
    }

    return [String(rawValue)];
}

function resolveArrayValues(values, preferredKey, fallbackKey) {
    return (values || [])
        .map(item => {
            if (item && typeof item === 'object') {
                return (
                    item[preferredKey] ||
                    (fallbackKey ? item[fallbackKey] : null) ||
                    item.id ||
                    item.name ||
                    ''
                );
            }

            return item;
        })
        .filter(value => value !== null && value !== undefined && value !== '')
        .map(String);
}

function toIntegerArray(values) {
    return (values || [])
        .map(value => Number(value))
        .filter(value => Number.isInteger(value));
}

function toStringArray(values) {
    return (values || []).map(String).filter(Boolean);
}

function setParameterIfPresent(parameters, key, values) {
    if (Array.isArray(values) && values.length > 0) {
        parameters[key] = values;
    }
}

function setDateRangeParameters(parameters, fromDate, toDate) {
    if (!fromDate) {
        return;
    }

    const safeToDate = toDate || fromDate;
    parameters.startDate = `${fromDate}T00:00:00Z`;
    parameters.endDate = `${safeToDate}T23:59:59Z`;
}

function getLegacyReportIdFromDefinitionCode(code) {
    const definitionCodeToLegacyId = {
        [ReportDefinitionCodes.EVENT_LOG]: '1',
        [ReportDefinitionCodes.USER_REPORT]: '2',
        [ReportDefinitionCodes.INDIVIDUAL_REPORT]: '3',
        [ReportDefinitionCodes.MOST_VISITED_PAGES]: '4',
        [ReportDefinitionCodes.MOST_CLICKED_CONTENT]: '5',
    };
    return (
        definitionCodeToLegacyId[
            String(code || '')
                .trim()
                .toLowerCase()
        ] || ''
    );
}

function getReportTypeLabelFromDefinitionCode(code) {
    const normalizedCode = String(code || '')
        .trim()
        .toLowerCase();
    if (normalizedCode === ReportDefinitionCodes.EVENT_LOG) {
        return 'Event Log';
    }
    if (normalizedCode === ReportDefinitionCodes.USER_REPORT) {
        return 'Users';
    }
    if (normalizedCode === ReportDefinitionCodes.INDIVIDUAL_REPORT) {
        return 'Individuals';
    }
    if (normalizedCode === ReportDefinitionCodes.MOST_VISITED_PAGES) {
        return 'Most Visited Pages';
    }
    if (normalizedCode === ReportDefinitionCodes.MOST_CLICKED_CONTENT) {
        return 'Most Clicked Content';
    }
    return '';
}

function buildCreateReportParameters(
    reportDefinitionCode,
    values,
    fromDate = null,
    toDate = null,
) {
    const parameters = {};

    if (fromDate && toDate)
        setDateRangeParameters(parameters, fromDate, toDate);

    if (reportDefinitionCode === ReportDefinitionCodes.EVENT_LOG) {
        setParameterIfPresent(
            parameters,
            'eventTypeIds',
            toIntegerArray(
                resolveArrayValues(values.eventLog.selectedEventTypes),
            ),
        );
        setParameterIfPresent(
            parameters,
            'severityIds',
            toIntegerArray(
                resolveArrayValues(values.eventLog.selectedSeverities),
            ),
        );
        setParameterIfPresent(
            parameters,
            'individualIds',
            toIntegerArray(
                resolveArrayValues(values.eventLog.selectedIndividuals),
            ),
        );
        setParameterIfPresent(
            parameters,
            'browsers',
            resolveArrayValues(values.eventLog.selectedBrowsers, 'name'),
        );
        setParameterIfPresent(
            parameters,
            'deviceTypes',
            resolveArrayValues(
                values.eventLog.selectedDeviceTypes,
                'apiValue',
                'name',
            ),
        );
        setParameterIfPresent(
            parameters,
            'portalIds',
            toIntegerArray(resolveArrayValues(values.eventLog.selectedPortals)),
        );
        setParameterIfPresent(
            parameters,
            'containerIds',
            toIntegerArray(
                resolveArrayValues(values.eventLog.selectedContainers),
            ),
        );
    }

    if (reportDefinitionCode === ReportDefinitionCodes.USER_REPORT) {
        setParameterIfPresent(
            parameters,
            'userTypeIds',
            toStringArray(values.users.userTypes),
        );
        setParameterIfPresent(
            parameters,
            'categoryIds',
            toIntegerArray(values.users.categories),
        );
        setParameterIfPresent(
            parameters,
            'userStatusIds',
            toIntegerArray(values.users.userStatuses),
        );
    }

    if (reportDefinitionCode === ReportDefinitionCodes.INDIVIDUAL_REPORT) {
        setParameterIfPresent(
            parameters,
            'populationGroupIds',
            toIntegerArray(values.individuals.populationGroups),
        );
        setParameterIfPresent(
            parameters,
            'categoryIds',
            toIntegerArray(values.individuals.categories),
        );
        setParameterIfPresent(
            parameters,
            'employmentStatusIds',
            toIntegerArray(values.individuals.employmentStatuses),
        );
        setParameterIfPresent(
            parameters,
            'accountStatusIds',
            toIntegerArray(values.individuals.accountStatuses),
        );
    }

    if (reportDefinitionCode === ReportDefinitionCodes.MOST_VISITED_PAGES) {
        setParameterIfPresent(
            parameters,
            'populationGroupIds',
            toIntegerArray(values.mostVisitedPages.populationGroups),
        );
    }

    if (reportDefinitionCode === ReportDefinitionCodes.MOST_CLICKED_CONTENT) {
        setParameterIfPresent(
            parameters,
            'populationGroupIds',
            toIntegerArray(values.mostClickedContent.populationGroups),
        );
    }

    return parameters;
}

function findDefinitionByRouteId(routeId, definitions) {
    if (!routeId) {
        return null;
    }

    const normalizedRouteId = normalizeLegacyReportId(routeId);

    const byDefinitionId = (definitions || []).find(
        definition => String(definition.id) === normalizedRouteId,
    );
    if (byDefinitionId) {
        return byDefinitionId;
    }

    return (definitions || []).find(definition => {
        return (
            getLegacyReportIdFromDefinitionCode(definition.code) ===
            normalizedRouteId
        );
    });
}

function getEventLogInitialParameters(reportDetails) {
    if (reportDetails?.eventLogParameters) {
        return reportDetails.eventLogParameters;
    }

    return {
        selectedEventTypes: [],
        selectedSeverities: [],
        selectedIndividuals: [],
        selectedBrowsers: [],
        selectedDeviceTypes: [],
        selectedPortals: [],
        selectedContainers: [],
    };
}

function getEmptyEventLogArrayParameters() {
    return {
        selectedEventTypes: [],
        selectedSeverities: [],
        selectedIndividuals: [],
        selectedBrowsers: [],
        selectedDeviceTypes: [],
        selectedPortals: [],
        selectedContainers: [],
    };
}

function mapApiParametersToEventLogFormat(parameters) {
    if (!parameters) {
        return getEmptyEventLogArrayParameters();
    }

    return {
        selectedEventTypes: parameters.eventTypeIds || [],
        selectedSeverities: parameters.severityIds || [],
        selectedIndividuals: parameters.individualIds || [],
        selectedBrowsers: parameters.browsers || [],
        selectedDeviceTypes: parameters.deviceTypes || [],
        selectedPortals: parameters.portalIds || [],
        selectedContainers: parameters.containerIds || [],
    };
}

function GenerateReportPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { reportId, savedReportId } = useParams();
    const { user } = useAuthContext();
    const { isAdminAudienceEnabled } = useAdminAudienceEnabled();

    const [viewerAudienceLabel, setViewerAudienceLabel] = useState('');
    const hasViewerDataFilter =
        isAdminAudienceEnabled && Boolean(user?.jwt?.df);

    const [definitions, setDefinitions] = useState([]);
    const [definitionsLoaded, setDefinitionsLoaded] = useState(false);
    const [generatedReportDetails, setGeneratedReportDetails] = useState(null);

    const [reportName, setReportName] = useState('');
    const [accessibleByAdmins, setAccessibleByAdmins] = useState([]);
    const [clientAdminOptions, setClientAdminOptions] = useState([]);
    const [fromDate, setFromDate] = useState(
        format(subDays(new Date(), 90), 'yyyy-MM-dd'),
    );
    const [toDate, setToDate] = useState(format(new Date(), 'yyyy-MM-dd'));
    const [isGenerating, setIsGenerating] = useState(false);
    const [displayDateRange, setDisplayDateRange] = useState(false);

    const [individualParameterOptions, setIndividualParameterOptions] =
        useState({
            populationGroups: [],
            categories: [],
            employmentStatuses: [],
            accountStatuses: [],
        });
    const [individualParameters, setIndividualParameters] = useState({
        populationGroups: [],
        categories: [],
        employmentStatuses: [],
        accountStatuses: [],
    });
    const [usersParameterOptions, setUsersParameterOptions] = useState({
        userStatuses: [],
        categories: [],
        userTypes: [],
    });
    const [usersParameters, setUsersParameters] = useState({
        userTypes: [],
        userStatuses: [],
    });
    const [mostVisitedPagesParameters, setMostVisitedPagesParameters] =
        useState({
            populationGroups: [],
        });
    const [mostClickedContentParameters, setMostClickedContentParameters] =
        useState({
            populationGroups: [],
        });
    const [eventLogParameters, setEventLogParameters] = useState(
        getEmptyEventLogArrayParameters(),
    );

    const isReadOnlyMode = useMemo(
        function getIsReadOnlyMode() {
            return Boolean(savedReportId) || Boolean(location.state?.readOnly);
        },
        [savedReportId, location.state],
    );

    const eventLogInitialParameters = useMemo(() => {
        if (isReadOnlyMode && generatedReportDetails?.parameters) {
            return mapApiParametersToEventLogFormat(
                generatedReportDetails.parameters,
            );
        }

        if (isReadOnlyMode) {
            return getEmptyEventLogArrayParameters();
        }

        return getEventLogInitialParameters(location.state?.reportDetails);
    }, [isReadOnlyMode, location.state?.reportDetails, generatedReportDetails]);

    const selectedDefinition = useMemo(
        function getSelectedDefinition() {
            const routeDefinition = findDefinitionByRouteId(
                reportId,
                definitions,
            );
            if (routeDefinition) {
                return routeDefinition;
            }

            const stateDefinitionId =
                location.state?.reportDetails?.reportDefinitionId ||
                location.state?.reportDetails?.id;

            const stateDefinition = findDefinitionByRouteId(
                stateDefinitionId,
                definitions,
            );
            if (stateDefinition) {
                return stateDefinition;
            }

            const detailsDefinition = findDefinitionByRouteId(
                generatedReportDetails?.reportDefinitionId,
                definitions,
            );
            if (detailsDefinition) {
                return detailsDefinition;
            }

            return definitions[0] || null;
        },
        [reportId, definitions, location.state, generatedReportDetails],
    );

    const activeReportId = useMemo(
        function getActiveLegacyReportId() {
            return getLegacyReportIdFromDefinitionCode(
                selectedDefinition?.code,
            );
        },
        [selectedDefinition],
    );

    const reportTypeLabel = useMemo(
        function getReportTypeLabel() {
            const labelFromCode = getReportTypeLabelFromDefinitionCode(
                selectedDefinition?.code,
            );
            if (labelFromCode) {
                return labelFromCode;
            }

            return selectedDefinition?.name || '';
        },
        [selectedDefinition],
    );

    useEffect(() => {
        if (activeReportId != 2 && activeReportId != 3) {
            setDisplayDateRange(true);
        } else {
            setDisplayDateRange(false);
        }
    }, [activeReportId]);

    useEffect(() => {
        let isCancelled = false;

        async function loadDefinitions() {
            try {
                const fetchedDefinitions = await fetchReportDefinitions();
                if (!isCancelled) {
                    setDefinitions(fetchedDefinitions || []);
                }
            } catch (error) {
                if (!isCancelled) {
                    setDefinitions([]);
                    Toast.error(getText('msg_48b787d1db53'));
                }
            } finally {
                if (!isCancelled) {
                    setDefinitionsLoaded(true);
                }
            }
        }

        loadDefinitions();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        async function loadGeneratedReportDetails() {
            if (!savedReportId) {
                setGeneratedReportDetails(null);
                return;
            }

            try {
                const fetchedGeneratedReport =
                    await fetchGeneratedReportById(savedReportId);
                if (!isCancelled) {
                    setGeneratedReportDetails(fetchedGeneratedReport);
                    if (fetchedGeneratedReport?.name) {
                        setReportName(fetchedGeneratedReport.name);
                    }
                }
            } catch (error) {
                if (!isCancelled) {
                    setGeneratedReportDetails(null);
                }
            }
        }

        loadGeneratedReportDetails();

        return () => {
            isCancelled = true;
        };
    }, [savedReportId]);

    useEffect(() => {
        if (isReadOnlyMode) {
            return;
        }

        const detailsFromState = location.state?.reportDetails || {};
        const fallbackFromDate = format(subDays(new Date(), 90), 'yyyy-MM-dd');
        const fallbackToDate = format(new Date(), 'yyyy-MM-dd');

        const nextReportName =
            detailsFromState.reportName ||
            selectedDefinition?.name ||
            `Report ${selectedDefinition?.id || ''}`;

        setReportName(nextReportName || '');
        setAccessibleByAdmins(
            mapAdminNamesToOptions(
                detailsFromState.accessibleBy,
                clientAdminOptions,
            ),
        );
        setFromDate(detailsFromState.fromDate || fallbackFromDate);
        setToDate(detailsFromState.toDate || fallbackToDate);
        setEventLogParameters(getEventLogInitialParameters(detailsFromState));

        setIndividualParameters({
            populationGroups: getArraySelection(
                detailsFromState.populationGroups,
            ),
            categories: getArraySelection(detailsFromState.categories),
            employmentStatuses: getArraySelection(
                detailsFromState.employmentStatuses,
            ),
            accountStatuses: getArraySelection(
                detailsFromState.accountStatuses,
            ),
        });
        setUsersParameters({
            userTypes: getArraySelection(detailsFromState.userTypes),
            userStatuses: getArraySelection(detailsFromState.userStatuses),
        });
        setMostVisitedPagesParameters({
            populationGroups: getArraySelection(
                detailsFromState.populationGroups,
            ),
        });
        setMostClickedContentParameters({
            populationGroups: getArraySelection(
                detailsFromState.populationGroups,
            ),
        });
    }, [
        location.state,
        selectedDefinition,
        isReadOnlyMode,
        clientAdminOptions,
    ]);

    useEffect(() => {
        if (!isReadOnlyMode || !generatedReportDetails) {
            return;
        }

        const params = generatedReportDetails.parameters || {};

        if (generatedReportDetails.name) {
            setReportName(generatedReportDetails.name);
        }

        if (params.startDate) {
            setFromDate(format(new Date(params.startDate), 'yyyy-MM-dd'));
        }
        if (params.endDate) {
            setToDate(format(new Date(params.endDate), 'yyyy-MM-dd'));
        }

        setAccessibleByAdmins(
            (generatedReportDetails.sharedWithIndividuals || []).map(
                individual => ({
                    id: individual.id,
                    name: individual.name,
                }),
            ),
        );

        setIndividualParameters({
            populationGroups: (params.populationGroupIds || []).map(String),
            categories: (params.categoryIds || []).map(String),
            employmentStatuses: (params.employmentStatusIds || []).map(String),
            accountStatuses: (params.accountStatusIds || []).map(String),
        });

        setUsersParameters({
            userTypes: (params.userTypeIds || []).map(String),
            categories: (params.categoryIds || []).map(String),
            userStatuses: (params.userStatusIds || []).map(String),
        });

        setMostVisitedPagesParameters({
            populationGroups: (params.populationGroupIds || []).map(String),
        });

        setMostClickedContentParameters({
            populationGroups: (params.populationGroupIds || []).map(String),
        });
    }, [isReadOnlyMode, generatedReportDetails]);

    useEffect(() => {
        let isCancelled = false;

        async function loadClientAdminUsers() {
            try {
                const fetchedClientAdminUsers = await fetchClientAdminUsers();

                if (isCancelled) {
                    return;
                }

                const formattedAdminUsers = (fetchedClientAdminUsers || [])
                    .map(adminUser => ({
                        id: adminUser.individualId,
                        name: `${adminUser.firstName} ${adminUser.lastName}`,
                    }))
                    .filter(adminUser => {
                        return adminUser.id !== user?.jwt?.ind;
                    });

                setClientAdminOptions(formattedAdminUsers);
            } catch (error) {
                if (!isCancelled) {
                    setClientAdminOptions([]);
                }
            }
        }

        loadClientAdminUsers();

        return () => {
            isCancelled = true;
        };
    }, []);

    useEffect(() => {
        let isCancelled = false;

        async function loadAudienceLabel() {
            if (!hasViewerDataFilter) {
                return;
            }

            try {
                const groups = await fetchPopulationGroups();
                const defaultGroup = groups.find(
                    group => group.id === user?.jwt?.df,
                );

                if (!isCancelled) {
                    setViewerAudienceLabel(
                        defaultGroup?.name || String(user?.jwt?.df || ''),
                    );
                }
            } catch (error) {
                if (!isCancelled) {
                    setViewerAudienceLabel(String(user?.jwt?.df || ''));
                }
            }
        }

        loadAudienceLabel();

        return () => {
            isCancelled = true;
        };
    }, [hasViewerDataFilter, user?.jwt?.df]);

    useEffect(() => {
        if (activeReportId !== '2') {
            setUsersParameterOptions({
                userStatuses: [],
                categories: [],
                userTypes: [],
            });
            return;
        }

        let isCancelled = false;

        async function loadUsersParameterOptions() {
            try {
                const [userStatuses, categories] = await Promise.all([
                    fetchTenantUserStatuses(),
                    fetchEmploymentCategories(),
                ]);

                if (isCancelled) {
                    return;
                }

                const userTypeLabels = {
                    MultiClient: 'Multi Tenant User',
                    ClientAdmin: 'Client Admin',
                    Individual: 'Individual',
                    LimitedUser: 'Additional User',
                };

                const userTypeOptions = Object.entries(UserType).map(
                    ([name, id]) => ({
                        id: String(id),
                        name: userTypeLabels[name] || name,
                    }),
                );

                setUsersParameterOptions({
                    userStatuses: (userStatuses || []).map(status => ({
                        id: String(status.id),
                        name: String(status.name || status.id),
                    })),
                    categories: (categories || []).map(category => ({
                        id: String(category.id),
                        name: String(category.name || category.id),
                    })),
                    userTypes: userTypeOptions,
                });
            } catch (error) {
                if (!isCancelled) {
                    setUsersParameterOptions({
                        userStatuses: [],
                        categories: [],
                        userTypes: [],
                    });
                }
            }
        }

        loadUsersParameterOptions();

        return () => {
            isCancelled = true;
        };
    }, [activeReportId]);

    useEffect(() => {
        if (!['3', '4', '5'].includes(activeReportId)) {
            setIndividualParameterOptions({
                populationGroups: [],
                categories: [],
                employmentStatuses: [],
                accountStatuses: [],
            });
            return;
        }

        let isCancelled = false;

        async function loadIndividualParameterOptions() {
            try {
                const [
                    groups,
                    categories,
                    employmentStatuses,
                    accountStatuses,
                ] = await Promise.all([
                    fetchPopulationGroups(),
                    fetchEmploymentCategories(),
                    fetchEmploymentStatuses(),
                    fetchTenantUserStatuses(),
                ]);

                if (isCancelled) {
                    return;
                }

                setIndividualParameterOptions({
                    populationGroups: (groups || []).map(group => ({
                        id: String(group.id),
                        name: String(group.name || group.id),
                    })),
                    categories: (categories || []).map(category => ({
                        id: String(category.id),
                        name: String(category.name || category.id),
                    })),
                    employmentStatuses: (employmentStatuses || []).map(
                        status => ({
                            id: String(status.id),
                            name: String(status.name || status.id),
                        }),
                    ),
                    accountStatuses: (accountStatuses || []).map(status => ({
                        id: String(status.id),
                        name: String(status.name || status.id),
                    })),
                });
            } catch (error) {
                if (!isCancelled) {
                    setIndividualParameterOptions({
                        populationGroups: [],
                        categories: [],
                        employmentStatuses: [],
                        accountStatuses: [],
                    });
                }
            }
        }

        loadIndividualParameterOptions();

        return () => {
            isCancelled = true;
        };
    }, [activeReportId]);

    function handleClose() {
        navigate(ROUTES.REPORTS_LIST);
    }

    function handleAccessibleByChange(adminUsers) {
        if (isReadOnlyMode) {
            return;
        }

        setAccessibleByAdmins(adminUsers);
    }

    function handleReportNameChange(event) {
        if (isReadOnlyMode) {
            return;
        }

        setReportName(event.target.value);
    }

    function handleFromDateChange(date) {
        if (isReadOnlyMode) {
            return;
        }

        setFromDate(date);
        if (toDate && new Date(date) > new Date(toDate)) {
            setToDate(null);
            return;
        }

        if (toDate && date === toDate) {
            setToDate(null);
        }
    }

    function handleToDateChange(date) {
        if (isReadOnlyMode) {
            return;
        }

        if (date === fromDate) {
            setToDate(null);
            return;
        }

        setToDate(date);
    }

    function handleIndividualsParameterChange(field, selectedOptions) {
        if (isReadOnlyMode) {
            return;
        }

        setIndividualParameters(previousParameters => ({
            ...previousParameters,
            [field]: (selectedOptions || [])
                .map(option => option?.id)
                .filter(Boolean)
                .map(String),
        }));
    }

    function handleUsersParameterChange(field, selectedOptions) {
        if (isReadOnlyMode) {
            return;
        }

        setUsersParameters(previousParameters => ({
            ...previousParameters,
            [field]: (selectedOptions || [])
                .map(option => option?.id)
                .filter(Boolean)
                .map(String),
        }));
    }

    function handleMostVisitedPagesPopulationGroupsChange(selectedOptions) {
        if (isReadOnlyMode) {
            return;
        }

        setMostVisitedPagesParameters({
            populationGroups: (selectedOptions || [])
                .map(option => option?.id)
                .filter(Boolean)
                .map(String),
        });
    }

    function handleMostClickedContentPopulationGroupsChange(selectedOptions) {
        if (isReadOnlyMode) {
            return;
        }

        setMostClickedContentParameters({
            populationGroups: (selectedOptions || [])
                .map(option => option?.id)
                .filter(Boolean)
                .map(String),
        });
    }

    async function handleGenerateReport() {
        if (!selectedDefinition?.code) {
            Toast.error(getText('msg_77955cd46e06'));
            return;
        }

        if (isGenerating || isReadOnlyMode) {
            return;
        }

        const sharedWithIndividualIds = toIntegerArray(
            (accessibleByAdmins || []).map(admin => admin?.id),
        );

        const parameters = buildCreateReportParameters(
            selectedDefinition.code,
            {
                eventLog: eventLogParameters,
                users: usersParameters,
                individuals: individualParameters,
                mostVisitedPages: mostVisitedPagesParameters,
                mostClickedContent: mostClickedContentParameters,
            },
            displayDateRange ? fromDate : null,
            displayDateRange ? toDate : null,
        );

        const payload = {
            reportDefinitionCode: selectedDefinition.code,
            name: reportName,
            parameters,
            sharedWithIndividualIds,
        };

        setIsGenerating(true);

        try {
            await createGeneratedReport(payload);
            await logEvent(
                LogEventType.LogReportRan,
                `Report generated: ${selectedDefinition.code}`,
            );
            Toast.success(getText('msg_ff33155f319d'));
            navigate(ROUTES.REPORTS_HISTORY);
        } catch (error) {
            Toast.error(getText('msg_1a4b111e0aa7'));
        } finally {
            setIsGenerating(false);
        }
    }

    function renderReportSpecificContent() {
        if (activeReportId === '1') {
            return (
                <div>
                    <div
                        className="event-log-form"
                        style={{ padding: '0 24px' }}
                    >
                        <EventLogReportParameters
                            initialParameters={eventLogInitialParameters}
                            readOnly={isReadOnlyMode}
                            onParametersChange={setEventLogParameters}
                        />
                    </div>
                </div>
            );
        }

        if (activeReportId === '2') {
            return (
                <UsersReportParameters
                    isReadOnlyMode={isReadOnlyMode}
                    usersParameterOptions={usersParameterOptions}
                    usersParameters={usersParameters}
                    handleUsersParameterChange={handleUsersParameterChange}
                />
            );
        }

        if (activeReportId === '3') {
            return (
                <IndividualsReportParameters
                    isReadOnlyMode={isReadOnlyMode}
                    individualParameterOptions={individualParameterOptions}
                    individualParameters={individualParameters}
                    handleIndividualsParameterChange={
                        handleIndividualsParameterChange
                    }
                />
            );
        }

        if (activeReportId === '4') {
            return (
                <MostVisitedPagesReportParameters
                    isReadOnlyMode={isReadOnlyMode}
                    populationGroupOptions={
                        individualParameterOptions.populationGroups
                    }
                    selectedPopulationGroups={
                        mostVisitedPagesParameters.populationGroups
                    }
                    handlePopulationGroupsChange={
                        handleMostVisitedPagesPopulationGroupsChange
                    }
                />
            );
        }

        if (activeReportId === '5') {
            return (
                <MostClickedContentReportParameters
                    isReadOnlyMode={isReadOnlyMode}
                    populationGroupOptions={
                        individualParameterOptions.populationGroups
                    }
                    selectedPopulationGroups={
                        mostClickedContentParameters.populationGroups
                    }
                    handlePopulationGroupsChange={
                        handleMostClickedContentPopulationGroupsChange
                    }
                />
            );
        }

        return (
            <div className="px-4">
                <p className="page-description-text">
                    {getText('msg_9d6f5283c683')}
                </p>
            </div>
        );
    }

    if (!definitionsLoaded) {
        return (
            <div className="page-content">
                <div className="card">
                    <div className="card-body px-4 py-4">
                        <p className="page-description-text">
                            {getText('msg_53f91c39c0e4')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-start mb-4">
                        <div
                            style={{ display: 'flex', flexDirection: 'column' }}
                            className="mb-4"
                        >
                            <p className="page-title float-start card-title ms-4">
                                {`Report Parameters${reportTypeLabel ? ` - ${reportTypeLabel}` : ''}`}
                            </p>
                            <p
                                className="ing-portals-subtitle mt-1"
                                style={{ marginTop: '10px' }}
                            >
                                {isReadOnlyMode
                                    ? 'View report parameters saved for this generated report in read-only mode.'
                                    : 'Configure parameters and generate report output.'}
                            </p>
                        </div>

                        <div className="float-end d-flex align-items-center me-4">
                            {hasViewerDataFilter && !isReadOnlyMode && (
                                <span className="mx-3">
                                    <AudienceBadge
                                        label={viewerAudienceLabel}
                                    />
                                </span>
                            )}
                            <span
                                type="button"
                                className="ing-text-buttons event-log-close-btn m-0"
                                onClick={handleClose}
                            >
                                <i className="bi bi-x me-2" />
                                {getText('msg_bbfa773e5a63')}
                            </span>
                        </div>
                    </div>

                    <div className="px-4">
                        <div className="event-log-form" style={{ gap: '24px' }}>
                            <div
                                className="form-row center-row"
                                style={{ marginBottom: 0 }}
                            >
                                <label className="individuals-label-wrapper">
                                    <div
                                        className="file-form-label"
                                        style={
                                            isReadOnlyMode
                                                ? {
                                                      color: 'var(--iNGAGED-Neutral-Color2)',
                                                  }
                                                : {}
                                        }
                                    >
                                        {getText('msg_5e4a47a52795')}
                                    </div>
                                    {isReadOnlyMode ? (
                                        <p
                                            style={{
                                                textAlign: 'left',
                                                color: 'var(--iNGAGED-Neutral-Color)',
                                                fontSize: '16px',
                                                margin: 0,
                                            }}
                                        >
                                            {reportName}
                                        </p>
                                    ) : (
                                        <>
                                            <input
                                                id="reportName"
                                                className="form-control"
                                                value={reportName}
                                                onChange={
                                                    handleReportNameChange
                                                }
                                                style={{
                                                    height: '48px',
                                                    borderRadius: '12px',
                                                    border: 'solid 1px rgba(47, 43, 67, 0.2)',
                                                    paddingLeft: '16px',
                                                    paddingRight: '16px',
                                                }}
                                                maxLength="50"
                                                type="text"
                                                aria-label={getText('msg_efa9b9952dac')}
                                            />
                                            <CharacterLimitIndicator
                                                maxChars={50}
                                                inputStr={reportName || ''}
                                            />
                                        </>
                                    )}
                                </label>
                            </div>

                            {displayDateRange && (
                                <div
                                    className="form-row center-row"
                                    style={{ marginBottom: 0 }}
                                >
                                    <label className="individuals-label-wrapper">
                                        <div
                                            className="file-form-label"
                                            style={
                                                isReadOnlyMode
                                                    ? {
                                                          color: 'var(--iNGAGED-Neutral-Color2)',
                                                      }
                                                    : {}
                                            }
                                        >
                                            {getText('msg_6bb4b674b323')}
                                        </div>
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                gap: '16px',
                                            }}
                                        >
                                            <div>
                                                <div
                                                    style={{
                                                        marginBottom: '6px',
                                                        ...(isReadOnlyMode
                                                            ? {
                                                                  color: 'var(--iNGAGED-Neutral-Color2)',
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    {getText('msg_3f66052a107e')}
                                                </div>
                                                {isReadOnlyMode ? (
                                                    <p
                                                        style={{
                                                            fontSize: '16px',
                                                            color: 'var(--iNGAGED-Neutral-Color)',
                                                            margin: 0,
                                                        }}
                                                    >
                                                        {formatDateToMMDDYYYY(
                                                            fromDate,
                                                        )}
                                                    </p>
                                                ) : (
                                                    <CustomDatePicker
                                                        selectedDate={fromDate}
                                                        onDateChange={
                                                            handleFromDateChange
                                                        }
                                                        maxDate={new Date()}
                                                        hideErrorMessage
                                                    />
                                                )}
                                            </div>
                                            <div>
                                                <div
                                                    style={{
                                                        marginBottom: '6px',
                                                        ...(isReadOnlyMode
                                                            ? {
                                                                  color: 'var(--iNGAGED-Neutral-Color2)',
                                                              }
                                                            : {}),
                                                    }}
                                                >
                                                    {getText('msg_ae79ea1e9c63')}
                                                </div>
                                                {isReadOnlyMode ? (
                                                    <p
                                                        style={{
                                                            fontSize: '16px',
                                                            color: 'var(--iNGAGED-Neutral-Color)',
                                                            margin: 0,
                                                        }}
                                                    >
                                                        {formatDateToMMDDYYYY(
                                                            toDate || fromDate,
                                                        )}
                                                    </p>
                                                ) : (
                                                    <CustomDatePicker
                                                        selectedDate={
                                                            toDate || fromDate
                                                        }
                                                        onDateChange={
                                                            handleToDateChange
                                                        }
                                                        minDate={fromDate}
                                                        maxDate={new Date()}
                                                        hideErrorMessage
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </label>
                                </div>
                            )}

                            <div
                                style={{
                                    width: '100%',
                                    maxWidth: '550px',
                                }}
                            >
                                {renderReportSpecificContent()}
                            </div>

                            <div
                                style={{
                                    margin: '10px auto',
                                    width: '100%',
                                    maxWidth: '550px',
                                }}
                            >
                                <div
                                    className="file-form-label"
                                    style={
                                        isReadOnlyMode
                                            ? {
                                                  color: 'var(--iNGAGED-Neutral-Color2)',
                                              }
                                            : {}
                                    }
                                >
                                    {getText('msg_54034db83011')}
                                </div>
                                <SearchableDropdown
                                    placeholderLabel={getText('msg_b1f168d000c5')}
                                    options={clientAdminOptions}
                                    optionIDField="id"
                                    savedOptionIDField="id"
                                    updateOptions={handleAccessibleByChange}
                                    savedOptions={accessibleByAdmins}
                                    showTags
                                    readOnly={isReadOnlyMode}
                                />
                            </div>

                            {!isReadOnlyMode && (
                                <div
                                    style={{
                                        width: '100%',
                                        maxWidth: '550px',
                                        margin: '0 auto',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <button
                                        className="btn btn-primary"
                                        type="button"
                                        onClick={handleGenerateReport}
                                        disabled={isGenerating}
                                    >
                                        {getText('msg_fc45f9b7a9a6')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default GenerateReportPage;
